import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import type { OnDestroy } from '@angular/core';
import { downgradeComponent } from '@angular/upgrade/static';
import App from '@app/app';
import type { ISubject as CartSubject } from '@app/interface/cart/subject.interface';
import type { IShoppingCartStore } from '@app/interface/cart/shopping-cart-store.interface';
import type { LookImage } from '@app/interface/image/look-image.interface';
import type { IJobStore } from '@app/interface/job/job-store.interface';
import type { Subject } from '@app/interface/subject/subject.interface';
import { LtAvatarComponent } from '@app/components/core/lt-avatar/lt-avatar.component';
import { LtTranslatePipe } from '@app/pipes/lt-translate.pipe';
import { LtIconComponent } from '@app/components/core/lt-icon/lt-icon.component';
import { SubjectLookResolver } from '@app/service/image/subject-look.resolver';
import { StudentBannerService } from '@app/service/student-banner.service';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';

export enum BannerType {
  PRE_PIC_DAY = 'prePicDay',
  DEFAULT = 'default',
  HIDDEN = 'hidden'
}

type BannerState = {
  enabled: boolean;
  isPrePicDay: boolean;
  displayName: string;
  previewLook: LookImage | null;
  jobName: string;
  eventDate: string;
  accountName: string;
  gradYear?: number;
};

@Component({
  selector: 'lt-student-banner',
  standalone: true,
  imports: [CommonModule, LtTranslatePipe, LtIconComponent, LtAvatarComponent],
  templateUrl: './student-banner.template.html',
  styleUrl: './student-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StudentBannerComponent implements OnDestroy {
  private shoppingCartStore: IShoppingCartStore = inject(angularjs.ShoppingCartStore);
  private readonly subjectStore = inject(angularjs.SubjectStore);
  private readonly subjectLookResolver = inject(SubjectLookResolver);
  private readonly studentBannerService = inject(StudentBannerService);
  private readonly rootScope = inject(angularjs.$rootScope);
  private readonly jobStore: IJobStore = inject(angularjs.JobStore);
  private unwatch?: () => void;
  private readonly state = signal<BannerState>({
    enabled: false,
    isPrePicDay: false,
    displayName: '',
    previewLook: null,
    jobName: '',
    eventDate: '',
    accountName: ''
  });

  jobKey = input<string>();
  readonly jobNameInput = input<string>();
  readonly eventDateInput = input<string>();
  readonly accountNameInput = input<string>();

  readonly enabled = computed(() => this.state().enabled);
  readonly isPrePicDay = computed(() => this.state().isPrePicDay);
  readonly displayName = computed(() => this.state().displayName);
  readonly previewLook = computed(() => this.state().previewLook);
  readonly jobName = computed(() => this.jobNameInput() ?? this.state().jobName);
  readonly eventDate = computed(() => this.eventDateInput() ?? this.state().eventDate);
  readonly accountName = computed(() => this.accountNameInput() ?? this.state().accountName);

  readonly eventNameAndYear = computed(() =>
    this.studentBannerService.getEventNameAndYear(this.jobName(), this.eventDate(), this.state().gradYear)
  );
  readonly schoolName = computed(() => this.accountName());

  constructor() {
    this.syncState();

    if (typeof this.rootScope.$watch === 'function') {
      this.unwatch = this.rootScope.$watch(
        () => this.readBannerState(),
        (state) => this.state.set(state),
        true
      ) as () => void;
    }
  }

  ngOnDestroy(): void {
    this.unwatch?.();
  }

  private syncState(): void {
    this.state.set(this.readBannerState());
  }

  private resolveJobData(isPrePicDay: boolean): {
    jobName: string;
    eventDate: string;
    accountName: string;
    gradYear?: number;
  } {
    if (!isPrePicDay) return { jobName: '', eventDate: '', accountName: '' };
    const job = this.jobStore.getJob(this.jobKey());
    return {
      jobName: job?.jobName ?? '',
      eventDate: job?.eventDate ?? '',
      accountName: job?.accountName ?? '',
      gradYear: job?.gradYear
    };
  }

  private getCurrentBannerSubject(): CartSubject {
    return this.shoppingCartStore.getCurrentSubject();
  }

  private getDashboardSubject(currentSubject: CartSubject): Subject | null {
    const id = currentSubject.id;

    if (id) {
      return this.subjectStore.findSubjectBySubjectOrderId(id) ?? null;
    }

    return this.subjectStore.getSelectedSubject(false) ?? null;
  }

  private resolvePreviewLook(bannerType: BannerType): LookImage | null {
    if (bannerType !== BannerType.DEFAULT) return null;
    const currentSubject = this.getCurrentBannerSubject();
    const dashboardSubject = this.getDashboardSubject(currentSubject);
    const jobKey = currentSubject.portraitDashboard.jobKey;

    return this.subjectLookResolver.resolve(dashboardSubject, jobKey);
  }

  private readBannerState(): BannerState {
    const subject = this.shoppingCartStore.getCurrentSubject();
    if (!subject) {
      return {
        enabled: false,
        isPrePicDay: false,
        displayName: '',
        previewLook: null,
        jobName: '',
        eventDate: '',
        accountName: ''
      };
    }
    const displayName = subject.subjectFirstName?.trim() ?? '';
    const bannerType = this.jobKey() ? BannerType.PRE_PIC_DAY : this.studentBannerService.getBannerType();
    const enabled = bannerType !== BannerType.HIDDEN;
    const isPrePicDay = bannerType === BannerType.PRE_PIC_DAY;
    const { jobName, eventDate, accountName, gradYear: resolvedGradYear } = this.resolveJobData(isPrePicDay);
    const gradYear = resolvedGradYear ?? this.jobStore.getJobByJobKey(subject?.portraitDashboard?.jobKey)?.gradYear;
    const previewLook = this.resolvePreviewLook(bannerType);
    return { enabled, isPrePicDay, displayName, previewLook, jobName, eventDate, accountName, gradYear };
  }
}

App.directive('ltStudentBanner', downgradeComponent({ component: StudentBannerComponent }));
